import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
} from "react";
import echarts from "./echartsCore";
import { ensureTheme } from "./echartsTheme";

const formatNumber = (v) => {
  if (v === null || v === undefined || isNaN(v)) return "—";
  return Number(v).toLocaleString();
};

const buildTooltipFormatter = (valueFormatter) => (params) => {
  if (!Array.isArray(params) || params.length === 0) return "";
  const head = params[0].axisValueLabel || params[0].name || "";
  const total = params.reduce((sum, p) => sum + Number(p.value || 0), 0);

  const rows = params
    .map((p) => {
      const v = Number(p.value || 0);
      const pct = total > 0 ? Math.round((v / total) * 100) : 0;
      const display = valueFormatter ? valueFormatter(v) : formatNumber(v);
      return `
        <div style="display:flex;align-items:center;gap:8px;font-size:12px;line-height:1.5;">
          <span style="width:8px;height:8px;border-radius:50%;background:${p.color};flex-shrink:0;"></span>
          <span style="opacity:.75;flex:1;">${p.seriesName}</span>
          <span style="font-variant-numeric:tabular-nums;font-weight:600;">${display}</span>
          <span style="opacity:.55;font-size:11px;min-width:32px;text-align:right;">${pct}%</span>
        </div>`;
    })
    .join("");

  const totalDisplay = valueFormatter ? valueFormatter(total) : formatNumber(total);

  return `
    <div style="min-width:180px;">
      <div style="font-size:11px;opacity:.7;margin-bottom:8px;letter-spacing:.02em;text-transform:uppercase;">${head}</div>
      ${rows}
      <div style="border-top:1px solid rgba(255,255,255,.1);margin-top:8px;padding-top:8px;display:flex;justify-content:space-between;font-size:12px;">
        <span style="opacity:.7;">Total</span>
        <span style="font-variant-numeric:tabular-nums;font-weight:600;">${totalDisplay}</span>
      </div>
    </div>`;
};

const StatChart = ({
  type = "stackedBar",
  categories = [],
  series = [],
  hiddenSeries,
  height = 280,
  valueFormatter,
  ariaLabel,
}) => {
  const containerRef = useRef(null);
  const instanceRef = useRef(null);

  const option = useMemo(() => {
    const visibleSeries = hiddenSeries
      ? series.filter((s) => !hiddenSeries.has(s.name))
      : series;

    const baseSeries = visibleSeries.map((s, idx) => {
      const common = {
        name: s.name,
        type: type === "line" || type === "area" ? "line" : "bar",
        data: s.data,
        itemStyle: { color: s.color, borderRadius: [4, 4, 0, 0] },
        emphasis: {
          focus: "series",
          itemStyle: { color: s.color },
        },
        animationDuration: 600,
        animationDelay: idx * 40,
        animationEasing: "cubicOut",
      };

      if (type === "stackedBar") {
        return { ...common, stack: s.stack || "total", barMaxWidth: 28 };
      }
      if (type === "bar") {
        return { ...common, barMaxWidth: 28 };
      }
      if (type === "line" || type === "area") {
        return {
          ...common,
          smooth: true,
          symbol: "circle",
          symbolSize: 6,
          showSymbol: false,
          lineStyle: { width: 2, color: s.color },
          areaStyle:
            type === "area"
              ? { color: s.color, opacity: 0.12 }
              : undefined,
        };
      }
      return common;
    });

    return {
      tooltip: {
        trigger: "axis",
        axisPointer: { type: "shadow" },
        formatter: buildTooltipFormatter(valueFormatter),
        confine: true,
      },
      grid: {
        left: 4,
        right: 12,
        top: 16,
        bottom: 8,
        containLabel: true,
      },
      xAxis: {
        type: "category",
        data: categories,
        boundaryGap: true,
        axisLabel: {
          interval: "auto",
          hideOverlap: true,
        },
      },
      yAxis: {
        type: "value",
        minInterval: 1,
        axisLabel: {
          formatter: (v) =>
            Math.abs(v) >= 1000 ? `${(v / 1000).toFixed(1)}k` : v,
        },
      },
      series: baseSeries,
      animationDuration: 600,
      animationEasing: "cubicOut",
    };
  }, [type, categories, series, hiddenSeries, valueFormatter]);

  useLayoutEffect(() => {
    const themeName = ensureTheme();
    if (!containerRef.current) return undefined;
    const instance = echarts.init(containerRef.current, themeName, {
      renderer: "canvas",
    });
    instanceRef.current = instance;

    const resize = () => instance.resize();
    let ro;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(resize);
      ro.observe(containerRef.current);
    } else {
      window.addEventListener("resize", resize);
    }

    return () => {
      if (ro) ro.disconnect();
      else window.removeEventListener("resize", resize);
      instance.dispose();
      instanceRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (!instanceRef.current) return;
    instanceRef.current.setOption(option, { notMerge: true });
  }, [option]);

  return (
    <div
      ref={containerRef}
      className="rdc-chart"
      role="img"
      aria-label={ariaLabel}
      style={{ height }}
    />
  );
};

export default StatChart;
